import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import { QueryKey } from ':cloud/queries/types';
import { APIClientBase } from ':cloud/state/Api';
import { CreateOrUpdateProjectResponse, CreateProjectRequest } from ':cloud/types/service_proto';

async function createProject({
  name,
  organizationId,
}: CreateProjectRequest): Promise<CreateOrUpdateProjectResponse> {
  const path = `/organization-management/v1/organizations/${organizationId}/projects`;

  const requestBody = {
    name,
  };
  const { data } = await APIClientBase.post<CreateOrUpdateProjectResponse>(path, requestBody);
  return data;
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ name, organizationId }: CreateProjectRequest) =>
      createProject({ name, organizationId }),
    onSuccess: async (_, { organizationId }) => {
      await queryClient.invalidateQueries([QueryKey.projects]);
      await queryClient.invalidateQueries([
        QueryKey.accounts,
        QueryKey.accountItems,
        organizationId,
      ]);
    },
    onError(error) {
      if (error instanceof AxiosError) {
        if (error.response?.data.message === 'project would exceed limit') {
          toast.show('You have reached the maximum number of projects allowed.', {
            variant: 'negative',
          });
        } else {
          toast.show('Unable to create project. Please retry', {
            variant: 'negative',
          });
        }
      }
    },
    meta: {
      successMsg: 'Project successfully created',
    },
  });
}
